import React from "react";
import { 
  Truck, 
  Calendar, 
  MapPin, 
  Route, 
  Clock, 
  ChevronDown, 
  ChevronUp, 
  Printer, 
  CheckCircle2, 
  CircleDashed, 
  SkipForward,
  Map
} from "lucide-react";
import { format } from "date-fns";
import { fi } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { 
  LOAD_STATUS_LABELS, 
  LOAD_STATUS_COLORS,
  LoadStatus,
  InstallationStatus,
} from "@/config/constants";
import { LoadTasksMap } from "@/components/loads/LoadTasksMap";
import { DEPOT_LOCATION } from "@/config/locations";
import { LoadListItem, LoadInstallation } from "@/types/loads";
import { LoadInstallationItem } from "./LoadInstallationItem";
import { calculateStatusCounts } from "@/lib/load-helpers";
import { toMapTask } from "@/types/supabase-helpers";

interface LoadCardProps {
  load: LoadListItem;
  installations: LoadInstallation[];
  isExpanded: boolean;
  showMap: boolean;
  expandedInstallations: Set<string>;
  highlightedTaskId: string | null;
  onToggleExpand: (loadId: string) => void;
  onToggleMap: (loadId: string) => void;
  onToggleInstallationExpand: (installationId: string) => void;
  onUpdateLoadStatus: (loadId: string, status: LoadStatus) => void;
  onInstallationStatusChange: (installationId: string, status: InstallationStatus) => void;
  onTaskClick: (taskId: string) => void;
  onPrint: (load: LoadListItem, installations: LoadInstallation[]) => void;
}

export const LoadCard = React.memo(function LoadCard({
  load,
  installations,
  isExpanded,
  showMap,
  expandedInstallations,
  highlightedTaskId,
  onToggleExpand,
  onToggleMap,
  onToggleInstallationExpand,
  onUpdateLoadStatus,
  onInstallationStatusChange,
  onTaskClick,
  onPrint,
}: LoadCardProps) {
  const installerName = load.installer 
    ? `${load.installer.first_name || ""} ${load.installer.last_name || ""}`.trim() || "Nimetön"
    : "Ei asentajaa";

  // Calculate status counts using helper
  const { completed: completedCount, partial: partialCount, skipped: skippedCount, pending: pendingCount } = calculateStatusCounts(installations);

  return (
    <Card>
      <Collapsible open={isExpanded} onOpenChange={() => onToggleExpand(load.id)}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Truck className="h-5 w-5" />
                  Kuorma {format(new Date(load.load_date), "d.M.yyyy", { locale: fi })}
                </CardTitle>
                <Badge className={LOAD_STATUS_COLORS[load.status as LoadStatus]}>
                  {LOAD_STATUS_LABELS[load.status as LoadStatus]}
                </Badge>
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {installerName}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {installations.length} kohdetta
                </span>
                {load.total_distance_km && (
                  <span className="flex items-center gap-1">
                    <Route className="h-4 w-4" />
                    {load.total_distance_km} km
                  </span>
                )}
                {load.estimated_duration_min && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {Math.floor(load.estimated_duration_min / 60)}h {load.estimated_duration_min % 60}min
                  </span>
                )}
              </div>
              {/* Task status summary */}
              {installations.length > 0 && (
                <div className="flex items-center gap-2 pt-1">
                  {completedCount > 0 && (
                    <span className="flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                      <CheckCircle2 className="h-3 w-3" />
                      {completedCount} valmis
                    </span>
                  )}
                  {partialCount > 0 && (
                    <span className="flex items-center gap-1 text-xs text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">
                      <CircleDashed className="h-3 w-3" />
                      {partialCount} osittain
                    </span>
                  )}
                  {skippedCount > 0 && (
                    <span className="flex items-center gap-1 text-xs text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
                      <SkipForward className="h-3 w-3" />
                      {skippedCount} ohitettu
                    </span>
                  )}
                  {pendingCount > 0 && (
                    <span className="flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
                      {pendingCount} odottaa
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPrint(load, installations)}
                title="Tulosta reittilista"
              >
                <Printer className="h-4 w-4" />
              </Button>
              <Select
                value={load.status}
                onValueChange={(value) => onUpdateLoadStatus(load.id, value as LoadStatus)}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Suunniteltu</SelectItem>
                  <SelectItem value="in_progress">Käynnissä</SelectItem>
                  <SelectItem value="completed">Valmis</SelectItem>
                  <SelectItem value="cancelled">Peruutettu</SelectItem>
                </SelectContent>
              </Select>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm">
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-3">
            {/* Map toggle and map */}
            {installations.length > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => onToggleMap(load.id)}
                >
                  <Map className="h-4 w-4" />
                  {showMap ? "Piilota kartta" : "Näytä kartalla"}
                </Button>
                
                {showMap && (
                  <div className="h-64 rounded-lg overflow-hidden border">
                    <LoadTasksMap
                      tasks={installations.map(toMapTask)}
                      depotLocation={DEPOT_LOCATION}
                      routeGeometry={load.route_geometry}
                      onTaskClick={onTaskClick}
                    />
                  </div>
                )}
              </>
            )}
            
            {installations.length === 0 ? (
              <p className="text-sm text-muted-foreground">Ei kohteita</p>
            ) : (
              <div className="space-y-2">
                {installations.map((inst, index) => (
                  <LoadInstallationItem
                    key={inst.id}
                    installation={inst}
                    index={index}
                    isExpanded={expandedInstallations.has(inst.id)}
                    isHighlighted={highlightedTaskId === inst.id}
                    onToggleExpand={onToggleInstallationExpand}
                    onStatusChange={onInstallationStatusChange}
                  />
                ))}
              </div>
            )}
            {load.notes && (
              <p className="mt-3 text-sm text-muted-foreground">
                <strong>Muistiinpanot:</strong> {load.notes}
              </p>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
});
